/**
 * File Size Calculator Tool
 */

import bytes from 'bytes';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type Unit = 'B' | 'KB' | 'MB' | 'GB' | 'TB' | 'KiB' | 'MiB' | 'GiB' | 'TiB';

function parseSize(input: string): number {
  const text = String(input ?? '').trim();
  if (!text) return 0;

  const m = text.match(/^([+-]?[0-9]*\.?[0-9]+)\s*([a-zA-Z]{0,3})$/);
  if (!m) throw ToolError.validation('Enter a size like "1536", "1.5 MB", "10MiB"');

  const value = Number(m[1]);
  if (!Number.isFinite(value)) throw ToolError.validation('Invalid number');

  const unitRaw = (m[2] || 'B').trim();
  const unitUpper = unitRaw.toUpperCase();
  const unit = (unitUpper === '' ? 'B' : unitUpper) as Unit;

  // Preserve explicit decimal vs binary semantics.
  const dec: Record<string, number> = { B: 1, KB: 1e3, MB: 1e6, GB: 1e9, TB: 1e12 };
  const bin: Record<string, number> = { B: 1, KIB: 1024, MIB: 1024 ** 2, GIB: 1024 ** 3, TIB: 1024 ** 4 };

  if (dec[unit]) return value * dec[unit];
  if (bin[unit]) return value * bin[unit];

  // Fallback to bytes() parser for other common spellings (e.g. "1k", "1kb", "1mb").
  const bytesModule = bytes as unknown as Record<string, unknown>;
  const parsed = typeof bytesModule.parse === 'function'
    ? (bytesModule.parse as (s: string) => number | null)(text)
    : undefined;
  if (typeof parsed === 'number' && Number.isFinite(parsed)) return parsed;

  throw ToolError.validation('Unknown unit. Use B, KB, MB, GB, TB, KiB, MiB, GiB, TiB');
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return 'NaN';
  if (Math.abs(n) < 1) return String(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export const FileSizeCalculatorTool: ITransformerTool = {
  id: 'file-size-calculator',
  name: 'File Size Calculator',
  description: 'Convert between bytes and human-readable sizes (decimal + binary)',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['file', 'size', 'bytes', 'kb', 'mb', 'gb', 'convert'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const bytes = parseSize(input);

    const lines: string[] = [];
    lines.push(`Bytes: ${fmt(bytes)} B`);
    lines.push('');
    lines.push('Decimal (base 10):');
    lines.push(`- ${fmt(bytes / 1e3)} KB`);
    lines.push(`- ${fmt(bytes / 1e6)} MB`);
    lines.push(`- ${fmt(bytes / 1e9)} GB`);
    lines.push(`- ${fmt(bytes / 1e12)} TB`);
    lines.push('');
    lines.push('Binary (base 2):');
    lines.push(`- ${fmt(bytes / 1024)} KiB`);
    lines.push(`- ${fmt(bytes / 1024 ** 2)} MiB`);
    lines.push(`- ${fmt(bytes / 1024 ** 3)} GiB`);
    lines.push(`- ${fmt(bytes / 1024 ** 4)} TiB`);
    return lines.join('\n');
  },
};
